import { getConfig } from '@/app/config'
import { Language, languages, User } from '@/features/auth'
import { Customer } from '@/features/auth/domain/customer'

import { info } from '../logger'

const USERLANE_SCRIPT_URL = 'https://cdn.userlane.com/userlane.js'

// deferred until after login so the third-party script never loads for anonymous visitors (EN-676)
const loadUserlaneScript = (): void => {
  const w = window as typeof window & { UserlaneCommandObject?: string }
  w.UserlaneCommandObject = 'Userlane'
  const queue: Window['Userlane'] & { q?: IArguments[] } = function () {
    // eslint-disable-next-line prefer-rest-params
    ;(queue.q = queue.q ?? []).push(arguments)
  }
  window.Userlane = queue
  const script = document.createElement('script')
  script.async = true
  script.src = USERLANE_SCRIPT_URL
  document.head.appendChild(script)
}

export class UserlaneService {
  private appId: string
  private isInitialized: boolean
  private isScriptLoaded = false

  get isUserlaneActive(): boolean {
    return !!window.Userlane && this.isInitialized
  }

  constructor(appId: string) {
    this.appId = appId
    this.isInitialized = false
  }

  init(user: User, customer: Customer, loggedInAsCustomer: boolean) {
    if (!this.isScriptLoaded) {
      loadUserlaneScript()
      this.isScriptLoaded = true
    }

    const { Userlane } = window

    const isProduction = window.config.APP_ENV === 'production'
    const userlaneUserId = isProduction ? user.id : `${window.config.APP_ENV}-${user.id}`

    const userData = {
      segment: customer.segment || 'default',
      opsLoggedInAsUser: loggedInAsCustomer,
    }

    info('User is authenticated, initializing Userlane')

    try {
      Userlane('identify', userlaneUserId, userData)
      Userlane('lang', user.preferredLanguage || languages[0])
      Userlane('init', this.appId)

      this.isInitialized = true
    } catch (e) {
      info('Userlane is not initialized', e)
    }
  }

  start() {
    if (!this.isUserlaneActive) return

    const { Userlane } = window

    Userlane('start', this.appId)
  }

  changeLanguage(language: Language) {
    if (!this.isUserlaneActive) return

    const { Userlane } = window

    Userlane('lang', language)
    Userlane('init', this.appId)
  }

  hide() {
    if (!this.isUserlaneActive) return

    const { Userlane } = window

    Userlane('hide')
  }
}

export const userlane = new UserlaneService(getConfig().USERLANE_ID ?? '')
